const MAX_ERROR_MESSAGE_LENGTH = 200;
const GENERIC_ERROR_MESSAGE = 'Something went wrong.';

/**
 * The shape graphql-request's `ClientError` (thrown by
 * `GraphQLDataProvider.ExecuteGQL` on a server-side GraphQL error, e.g.
 * GRAPHQL_VALIDATION_FAILED) carries: `.response.errors` holds the real,
 * human-readable per-error messages, while its own inherited `.message`
 * concatenates the ENTIRE JSON response body plus a server stack trace —
 * never fit to show a member. `unknown`, not a narrower type, because a
 * caught value's shape is never guaranteed.
 */
interface GraphQLClientErrorLike {
    readonly response?: {
        readonly errors?: ReadonlyArray<{ readonly message?: unknown }>;
    };
    readonly message?: unknown;
}

function IsRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function Truncate(message: string): string {
    return message.length > MAX_ERROR_MESSAGE_LENGTH ? `${message.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…` : message;
}

/**
 * Turns a thrown/caught error into a short, member-facing message for a
 * "Couldn't load …" / "Couldn't search" alert. Every Research Accelerator
 * page previously rendered `error.message` directly, which for a GraphQL
 * validation failure is the full JSON response plus a server stack trace
 * (hundreds of characters, seen on screen for Home/Recommended/Search-
 * everything). Prefers the first GraphQL error's own `message`, falls back
 * to a plain `Error.message`, and finally a generic message — always capped
 * so a stack trace can never reach the screen even if this error shape
 * changes upstream.
 */
export function DescribeError(error: unknown): string {
    if (IsRecord(error)) {
        const graphQLError = error as GraphQLClientErrorLike;
        const firstMessage = graphQLError.response?.errors?.[0]?.message;
        if (typeof firstMessage === 'string' && firstMessage.length > 0) {
            return Truncate(firstMessage);
        }
    }

    if (error instanceof Error && error.message.length > 0) {
        return Truncate(error.message);
    }

    return GENERIC_ERROR_MESSAGE;
}
